/**
 * `/meetings` — a list and a month calendar over the same data.
 *
 * The **post-meeting prompt** is the part that earns its place: a meeting whose
 * end time has passed with no notes appears in a "needs attention" strip. The
 * moment notes are most accurate is minutes after the meeting, and the second
 * most accurate moment is when something reminds you.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarDays, Plus } from 'lucide-react';
import { MEETING_TYPES } from '@menlo/shared';
import { useApiQuery, type Paged, type Row } from '@/lib/queries';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MeetingDialog } from '@/features/meetings/MeetingDialog';
import { AccountPicker } from '@/features/pickers/AccountPicker';
import { Dialog } from '@/components/ui/dialog';
import { Field } from '@/components/ui/label';
import { formatDateTime, humanize, localDateKey } from '@/lib/format';
import { cn } from '@/lib/utils';

const TYPE_COLOURS: Record<string, string> = {
  call: 'bg-sky-100 text-sky-900',
  video: 'bg-indigo-100 text-indigo-900',
  onsite_visit: 'bg-emerald-100 text-emerald-900',
  design_review: 'bg-amber-100 text-amber-900',
  trade_show: 'bg-fuchsia-100 text-fuchsia-900',
  internal_review: 'bg-slate-100 text-slate-900',
  email_thread: 'bg-stone-100 text-stone-900',
};

export function MeetingsPage(): React.ReactElement {
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [month, setMonth] = useState(() => new Date());
  const [creatingFor, setCreatingFor] = useState<string | null>(null);
  const [choosingAccount, setChoosingAccount] = useState(false);

  const meetings = useApiQuery<Paged<Row>>(
    ['meetings'],
    '/api/meetings?pageSize=200&sort=-starts_at',
  );
  const items = meetings.data?.items ?? [];

  const needsNotes = items.filter((meeting) => {
    const ends = meeting['ends_at'] ?? meeting['starts_at'];
    return (
      typeof ends === 'string' &&
      new Date(ends).getTime() < Date.now() &&
      (meeting['notes'] === null || meeting['notes'] === undefined || meeting['notes'] === '')
    );
  });

  const byDay = useMemo(() => {
    const grouped = new Map<string, Row[]>();
    for (const meeting of items) {
      // The local day, matching the calendar's local cells — not the UTC day.
      const day = localDateKey(String(meeting['starts_at']));
      grouped.set(day, [...(grouped.get(day) ?? []), meeting]);
    }
    return grouped;
  }, [items]);

  const visible = selectedDay === null ? items : (byDay.get(selectedDay) ?? []);

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between gap-2">
        <div>
          <h1 className="text-lg font-semibold">Meetings</h1>
          <p className="text-xs text-muted-foreground">
            {items.length} recent. A meeting with a customer attendee is what clears the &ldquo;no
            contact&rdquo; signal.
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setSelectedDay(null)}>
            <CalendarDays className="h-3.5 w-3.5" aria-hidden="true" />
            All days
          </Button>
          <Button size="sm" onClick={() => setChoosingAccount(true)}>
            <Plus className="h-3.5 w-3.5" aria-hidden="true" />
            Log a meeting
          </Button>
        </div>
      </header>

      {needsNotes.length > 0 && (
        <div className="rounded-md border border-amber-600/40 bg-amber-50 p-3 text-sm text-amber-950">
          <p className="font-medium">
            {needsNotes.length} meeting{needsNotes.length === 1 ? '' : 's'} finished without notes
          </p>
          <ul className="mt-1 space-y-0.5 text-xs">
            {needsNotes.slice(0, 5).map((meeting) => (
              <li key={String(meeting['id'])}>
                {String(meeting['subject'])} — {formatDateTime(meeting['starts_at'] as string)}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-[22rem_minmax(0,1fr)]">
        <MonthCalendar
          month={month}
          byDay={byDay}
          selectedDay={selectedDay}
          onSelectDay={setSelectedDay}
          onMonthChange={setMonth}
        />

        <div className="space-y-2">
          {selectedDay !== null && (
            <p className="text-xs text-muted-foreground">
              Showing {selectedDay}.{' '}
              <button
                type="button"
                className="underline underline-offset-2"
                onClick={() => setSelectedDay(null)}
              >
                Show everything
              </button>
            </p>
          )}

          <ul className="divide-y rounded-md border">
            {visible.map((meeting) => (
              <li key={String(meeting['id'])} className="px-3 py-2 text-sm">
                <p className="flex flex-wrap items-center gap-2">
                  <span
                    className={cn(
                      'rounded px-1.5 py-0.5 text-[11px]',
                      TYPE_COLOURS[String(meeting['meeting_type'])] ?? 'bg-muted',
                    )}
                  >
                    {humanize(String(meeting['meeting_type']))}
                  </span>
                  <span className="font-medium">{String(meeting['subject'])}</span>
                  {meeting['sentiment'] !== 'unknown' && (
                    <Badge variant="outline">{humanize(String(meeting['sentiment']))}</Badge>
                  )}
                </p>
                <p className="text-xs text-muted-foreground">
                  {formatDateTime(meeting['starts_at'] as string)}
                  {meeting['opportunity_id'] !== null &&
                    meeting['opportunity_id'] !== undefined && (
                      <>
                        {' · '}
                        <Link
                          to={`/opportunities/${String(meeting['opportunity_id'])}`}
                          className="underline underline-offset-2"
                        >
                          the deal
                        </Link>
                      </>
                    )}
                </p>
              </li>
            ))}
            {visible.length === 0 && (
              <li className="px-3 py-6 text-center text-xs text-muted-foreground">
                Nothing logged.
              </li>
            )}
          </ul>
        </div>
      </div>

      {choosingAccount && (
        <Dialog
          open
          onClose={() => setChoosingAccount(false)}
          size="sm"
          title="Which account?"
          description="A meeting always belongs to an account, even before it attaches to a deal."
        >
          <Field label="Account">
            <AccountPicker
              value={creatingFor}
              onChange={(value) => {
                setCreatingFor(value);
                if (value !== null) setChoosingAccount(false);
              }}
            />
          </Field>
        </Dialog>
      )}

      {creatingFor !== null && !choosingAccount && (
        <MeetingDialog
          accountId={creatingFor}
          onClose={() => setCreatingFor(null)}
          onSaved={() => {
            setCreatingFor(null);
            void meetings.refetch();
          }}
        />
      )}
    </div>
  );
}

function MonthCalendar({
  month,
  byDay,
  selectedDay,
  onSelectDay,
  onMonthChange,
}: {
  month: Date;
  byDay: Map<string, Row[]>;
  selectedDay: string | null;
  onSelectDay: (day: string | null) => void;
  onMonthChange: (month: Date) => void;
}): React.ReactElement {
  const year = month.getFullYear();
  const monthIndex = month.getMonth();
  const first = new Date(year, monthIndex, 1);
  const startOffset = (first.getDay() + 6) % 7; // Monday-first
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();

  const cells: (string | null)[] = [
    ...Array.from({ length: startOffset }, () => null),
    ...Array.from({ length: daysInMonth }, (_unused, index) => {
      const day = String(index + 1).padStart(2, '0');
      return `${year}-${String(monthIndex + 1).padStart(2, '0')}-${day}`;
    }),
  ];

  return (
    <section className="rounded-lg border bg-card p-3">
      <header className="mb-2 flex items-center justify-between">
        <button
          type="button"
          aria-label="Previous month"
          className="rounded px-2 py-1 text-sm hover:bg-accent"
          onClick={() => onMonthChange(new Date(year, monthIndex - 1, 1))}
        >
          ‹
        </button>
        <h2 className="text-sm font-medium">
          {month.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
        </h2>
        <button
          type="button"
          aria-label="Next month"
          className="rounded px-2 py-1 text-sm hover:bg-accent"
          onClick={() => onMonthChange(new Date(year, monthIndex + 1, 1))}
        >
          ›
        </button>
      </header>

      <div className="grid grid-cols-7 gap-1 text-center text-[10px] text-muted-foreground">
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
          <span key={day}>{day}</span>
        ))}
      </div>

      <div className="mt-1 grid grid-cols-7 gap-1">
        {cells.map((day, index) => {
          if (day === null) return <span key={`pad-${index}`} />;
          const meetings = byDay.get(day) ?? [];
          return (
            <button
              key={day}
              type="button"
              onClick={() => onSelectDay(selectedDay === day ? null : day)}
              aria-pressed={selectedDay === day}
              className={cn(
                'flex aspect-square flex-col items-center justify-start rounded border p-1 text-[11px]',
                selectedDay === day
                  ? 'border-primary bg-accent'
                  : 'border-transparent hover:bg-accent',
              )}
            >
              <span>{Number(day.slice(8))}</span>
              <span className="mt-0.5 flex flex-wrap justify-center gap-0.5">
                {meetings.slice(0, 3).map((meeting, position) => (
                  <span
                    key={position}
                    title={String(meeting['subject'])}
                    className={cn(
                      'h-1.5 w-1.5 rounded-full',
                      (TYPE_COLOURS[String(meeting['meeting_type'])] ?? 'bg-muted').split(' ')[0],
                    )}
                  />
                ))}
              </span>
            </button>
          );
        })}
      </div>

      <ul className="mt-3 flex flex-wrap gap-1.5 text-[10px]">
        {MEETING_TYPES.map((type) => (
          <li key={type} className="flex items-center gap-1">
            <span
              className={cn('h-2 w-2 rounded-full', (TYPE_COLOURS[type] ?? '').split(' ')[0])}
            />
            {humanize(type)}
          </li>
        ))}
      </ul>
    </section>
  );
}
